import React, { useState } from "react";
import { Mail, Phone, MapPin, ShieldCheck, Building, Lock, AlertTriangle, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { format } from "@/lib/brand-tokens";
import { DEMO_MEMBER } from "@/lib/mock-data";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

// MEM-07 — Account & profile
export default function AccountPage() {
  const m = DEMO_MEMBER;
  const [phone, setPhone] = useState(format.phone(m.phone));
  const [contact, setContact] = useState(m.preferredContact || "email");
  const [mfaOpen, setMfaOpen] = useState(false);
  const [bankOpen, setBankOpen] = useState(false);
  const [deactivateOpen, setDeactivateOpen] = useState(false);

  return (
    <div data-testid="account-page" className="space-y-6 pb-10 max-w-[820px]">
      <div>
        <div className="eyebrow">Settings</div>
        <h1 className="font-brand text-display text-ink leading-tight mt-1">Account</h1>
        <p className="text-body-sm text-ink-muted mt-1">Member since {format.date(m.memberSince)} · {format.currency(m.lifetimeEarnings)} earned to date</p>
      </div>

      {/* Profile */}
      <section className="bg-surface border border-line rounded-xl p-7">
        <h2 className="font-brand text-h2 text-ink">Profile</h2>
        <p className="mt-1 text-caption text-ink-subtle">Identity fields are locked after KYC. Contact info can be updated anytime.</p>
        <dl className="mt-6 grid sm:grid-cols-2 gap-x-8 gap-y-5 text-body-sm">
          <Read icon={<Mail className="size-3.5" />} label="Legal name" value={`${m.firstName} ${m.lastName}`} sub="Locked after KYC" />
          <Read icon={<Mail className="size-3.5" />} label="Email" value={m.email} sub="Verified" />
          <Read icon={<MapPin className="size-3.5" />} label="Location" value={`${m.city}, ${m.state}`} sub="Locked after KYC" />
          <div>
            <Label className="text-caption font-semibold text-ink mb-1.5 block">Phone</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} className="font-mono" />
            <button onClick={() => toast.success("Phone updated")} className="mt-1.5 text-caption font-semibold text-brand-700 hover:text-brand-800">Save</button>
          </div>
        </dl>
        <div className="mt-6 pt-5 border-t border-line">
          <Label className="text-caption font-semibold text-ink mb-2 block">Preferred contact method</Label>
          <div className="inline-flex gap-2 p-0.5 bg-surface-alt rounded-md">
            {["email", "phone"].map((c) => (
              <button key={c} onClick={() => { setContact(c); toast.success(`Preferred contact: ${c}`); }}
                      className={`px-4 py-1.5 rounded text-caption font-medium capitalize transition-all ${
                        contact === c ? "bg-surface text-ink shadow-xs" : "text-ink-muted hover:text-ink"
                      }`}>{c}</button>
            ))}
          </div>
        </div>
      </section>

      {/* Bank */}
      <section className="bg-surface border border-line rounded-xl p-7">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="font-brand text-h2 text-ink">Bank account</h2>
            <p className="mt-1 text-caption text-ink-subtle">Where your ACH payouts land.</p>
          </div>
          <Button variant="outline" onClick={() => setBankOpen(true)} data-testid="change-bank-btn">Change bank</Button>
        </div>
        <div className="mt-5 bg-surface-alt border border-line rounded-md p-5 flex items-start gap-4">
          <div className="size-10 rounded-md bg-brand-50 text-brand-700 grid place-items-center"><Building className="size-5" /></div>
          <div className="flex-1 min-w-0">
            <div className="text-body-sm font-semibold text-ink">{m.bankName}</div>
            <div className="text-caption text-ink-muted font-mono">Routing 021•••322 · Account {format.bankLast4(m.accountLast4)} · {m.accountType}</div>
            <div className="mt-2 inline-flex items-center gap-1.5 text-caption text-ink-subtle">
              <Lock className="size-3" /> Encrypted at rest with AWS KMS
            </div>
          </div>
        </div>
      </section>

      {/* Security */}
      <section className="bg-surface border border-line rounded-xl p-7">
        <h2 className="font-brand text-h2 text-ink">Security</h2>
        <ul className="mt-5 divide-y divide-line/60">
          <SecurityItem
            icon={<ShieldCheck className="size-4 text-emerald-700" />}
            title="Two-factor authentication"
            sub={m.mfaEnabled ? "Enabled · authenticator app" : "Not enabled. We strongly recommend turning this on."}
            cta={m.mfaEnabled ? "Manage" : "Enable"}
            onClick={() => setMfaOpen(true)}
          />
          <SecurityItem
            icon={<Lock className="size-4 text-ink-muted" />}
            title="Password"
            sub="Last changed 47 days ago"
            cta="Change password"
            href="/auth/reset-password"
          />
          <SecurityItem
            icon={<Phone className="size-4 text-ink-muted" />}
            title="Active sessions"
            sub="Signed in on 2 devices · last MacBook 12 minutes ago"
            cta="View sessions"
          />
        </ul>
      </section>

      {/* Danger */}
      <section className="bg-surface border border-red-200 rounded-xl p-7">
        <h2 className="font-brand text-h2 text-red-700">Deactivate account</h2>
        <p className="mt-1 text-caption text-ink-subtle leading-relaxed max-w-lg">
          Stops new reservations immediately. Existing reservations are completed and paid out as scheduled. Bank details are purged 90 days after final payout.
        </p>
        <Button onClick={() => setDeactivateOpen(true)} data-testid="deactivate-btn"
                variant="outline" className="mt-4 border-red-200 text-red-700 hover:bg-red-50">
          Deactivate my account
        </Button>
      </section>

      <Dialog open={mfaOpen} onOpenChange={setMfaOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Enable two-factor authentication</DialogTitle>
            <DialogDescription>You&rsquo;ll be redirected to set up an authenticator app.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMfaOpen(false)}>Cancel</Button>
            <Button onClick={() => { setMfaOpen(false); window.location.assign("/auth/mfa-setup"); }} className="bg-brand-700 hover:bg-brand-800 text-white">Continue</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bankOpen} onOpenChange={setBankOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change bank account</DialogTitle>
            <DialogDescription>You&rsquo;ll need to re-verify the new account before payouts route to it. Pending payments stay with your current account.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBankOpen(false)}>Cancel</Button>
            <Button onClick={() => { setBankOpen(false); window.location.assign("/onboarding/bank"); }} className="bg-brand-700 hover:bg-brand-800 text-white">Add new bank</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deactivateOpen} onOpenChange={setDeactivateOpen}>
        <DialogContent>
          <DialogHeader>
            <div className="size-10 rounded-md bg-red-50 text-red-700 grid place-items-center mb-3"><AlertTriangle className="size-5" /></div>
            <DialogTitle>Deactivate your account?</DialogTitle>
            <DialogDescription>This is reversible within 30 days. After that, your account is permanently closed and bank data purged on schedule.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeactivateOpen(false)}>Cancel</Button>
            <Button onClick={() => { setDeactivateOpen(false); toast.error("Deactivation queued"); }} className="bg-red-600 hover:bg-red-700 text-white">
              <LogOut className="size-4 mr-1.5" /> Deactivate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Read({ icon, label, value, sub }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-caption text-ink-hint">{icon}{label}</div>
      <div className="mt-0.5 text-ink font-medium">{value}</div>
      {sub && <div className="text-caption text-ink-subtle">{sub}</div>}
    </div>
  );
}

function SecurityItem({ icon, title, sub, cta, onClick, href }) {
  const Wrap = ({ children }) => href
    ? <a href={href} className="block py-4 hover:bg-surface-alt/50 -mx-2 px-2 rounded-md transition-colors">{children}</a>
    : <button onClick={onClick} className="w-full block py-4 hover:bg-surface-alt/50 -mx-2 px-2 rounded-md transition-colors text-left">{children}</button>;
  return (
    <li>
      <Wrap>
        <div className="flex items-center gap-3">
          {icon}
          <div className="flex-1 min-w-0">
            <div className="text-body-sm font-semibold text-ink">{title}</div>
            <div className="text-caption text-ink-subtle">{sub}</div>
          </div>
          <span className="text-caption font-semibold text-brand-700 hover:text-brand-800">{cta}</span>
        </div>
      </Wrap>
    </li>
  );
}
